import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { FileText, Plus, Eye, Pencil, Trash2, Share2, Loader2, Search, X, Zap, Flame } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getPartnerConfig } from '../config/partners';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [energyFilter, setEnergyFilter] = useState('all'); // 'all', 'gas', 'electricity'

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/offers`);
      setOffers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des offres');
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tri et filtrage des offres
  const filteredAndSortedOffers = useMemo(() => {
    let result = [...offers];
    
    // Filtrer par type d'énergie
    if (energyFilter !== 'all') {
      result = result.filter(offer => offer.energy_type === energyFilter);
    }
    
    // Filtrer par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(offer => {
        const siteName = offer.client_info?.site_name?.toLowerCase() || '';
        const clientName = offer.client_info?.name?.toLowerCase() || '';
        const title = offer.title?.toLowerCase() || '';
        const pce = offer.client_info?.pce_number?.toLowerCase() || '';
        const rae = offer.client_info?.rae_number?.toLowerCase() || '';
        const suppliers = offer.suppliers?.map(s => s.name?.toLowerCase()).join(' ') || '';
        
        return siteName.includes(query) || 
               clientName.includes(query) || 
               title.includes(query) || 
               pce.includes(query) ||
               rae.includes(query) ||
               suppliers.includes(query);
      });
    }
    
    // Trier par date (plus récent en premier)
    result.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
    
    return result;
  }, [offers, searchQuery, energyFilter]);

  const handleDelete = async (offerId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) {
      return;
    }

    try {
      setDeleting(offerId);
      await axios.delete(`${API}/offers/${offerId}`);
      toast.success('Offre supprimée avec succès');
      fetchOffers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error('Error deleting offer:', error);
    } finally {
      setDeleting(null);
    }
  };

  const copyShareLink = async (shareLink) => {
    const link = `${window.location.origin}/offer/${shareLink}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Lien copié dans le presse-papier');
    } catch (error) {
      // Fallback pour les navigateurs qui ne supportent pas l'API clipboard
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Lien copié dans le presse-papier');
      } catch (err) {
        toast.error('Impossible de copier le lien');
        console.error('Copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  // Compter les offres par type
  const gasCount = offers.filter(o => o.energy_type === 'gas' || !o.energy_type).length;
  const elecCount = offers.filter(o => o.energy_type === 'electricity').length;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/logos/place-des-energies.jpg" 
                alt="Place des Énergies" 
                className="h-12 sm:h-14 md:h-16"
              />
              <div>
                <p className="text-sm sm:text-base text-muted-foreground">Gestion des offres digitales</p>
              </div>
            </div>
            <Button
              data-testid="upload-new-offer-btn"
              onClick={() => navigate('/upload')}
              size="lg"
              className="rounded-sm hover:scale-[0.98] transition-transform"
            >
              <Plus className="mr-2 h-5 w-5" />
              Nouvelle offre
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-12">
        {/* Barre de recherche et filtres */}
        {!loading && offers.length > 0 && (
          <div className="mb-8 space-y-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Rechercher par nom, site, PCE, RAE, fournisseur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-10 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Filtres par type d'énergie */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-muted-foreground mr-2">Filtrer :</span>
              <button
                onClick={() => setEnergyFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  energyFilter === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="filter-all"
              >
                Toutes ({offers.length})
              </button>
              <button
                onClick={() => setEnergyFilter('gas')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  energyFilter === 'gas'
                    ? 'bg-orange-500 text-white'
                    : 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                }`}
                data-testid="filter-gas"
              >
                <Flame className="h-4 w-4" />
                Gaz ({gasCount})
              </button>
              <button
                onClick={() => setEnergyFilter('electricity')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  energyFilter === 'electricity'
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
                data-testid="filter-electricity"
              >
                <Zap className="h-4 w-4" />
                Électricité ({elecCount})
              </button>
              
              {/* Compteur de résultats */}
              <span className="ml-auto text-sm text-muted-foreground">
                {filteredAndSortedOffers.length} résultat{filteredAndSortedOffers.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : offers.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-20">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-heading mb-2">Aucune offre</h2>
            <p className="text-muted-foreground mb-6">
              Commencez par télécharger votre premier PDF d'offre
            </p>
            <Button onClick={() => navigate('/upload')} data-testid="empty-state-upload-btn">
              <Plus className="mr-2 h-4 w-4" />
              Télécharger une offre
            </Button>
          </div>
        ) : filteredAndSortedOffers.length === 0 ? (
          <div className="text-center py-20">
            <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-heading mb-2">Aucun résultat</h2>
            <p className="text-muted-foreground mb-6">
              Aucune offre ne correspond à votre recherche
            </p>
            <Button variant="outline" onClick={() => { setSearchQuery(''); setEnergyFilter('all'); }}>
              Réinitialiser les filtres
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedOffers.map((offer) => (
              <Card
                key={offer.id}
                data-testid={`offer-card-${offer.id}`}
                className="border border-border hover:shadow-lg transition-all duration-200 rounded-sm hover:-translate-y-1"
              >
                <CardHeader className="border-b border-border">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <CardTitle className="text-lg font-heading truncate flex-1 flex items-center gap-2">
                      {/* Icône type d'énergie */}
                      {offer.energy_type === 'electricity' ? (
                        <Zap className="h-5 w-5 text-blue-500 flex-shrink-0" />
                      ) : (
                        <Flame className="h-5 w-5 text-orange-500 flex-shrink-0" />
                      )}
                      <span className="truncate">{offer.title}</span>
                    </CardTitle>
                    {/* Badge du partenaire */}
                    <span 
                      className="px-3 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap"
                      style={{ backgroundColor: getPartnerConfig(offer.partner || 'place_des_energies').colors.primary }}
                    >
                      {getPartnerConfig(offer.partner || 'place_des_energies').name}
                    </span>
                  </div>
                  <CardDescription>
                    {offer.client_info.site_name}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        {offer.energy_type === 'electricity' ? 'RAE: ' : 'PCE: '}
                      </span>
                      <span className="font-mono-data">
                        {offer.energy_type === 'electricity' 
                          ? offer.client_info.rae_number 
                          : offer.client_info.pce_number}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Fournisseurs: </span>
                      <span className="font-semibold">{offer.suppliers.length}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Créé: </span>
                      <span>
                        {formatDistanceToNow(new Date(offer.created_at), {
                          addSuffix: true,
                          locale: fr,
                        })}
                      </span>
                    </div>
                  </div>
                </CardContent>
                <Separator />
                <CardFooter className="pt-4 flex gap-2">
                  <Button
                    data-testid={`view-offer-btn-${offer.id}`}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-sm"
                    onClick={() => navigate(`/offer/${offer.share_link}`)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Voir
                  </Button>
                  <Button
                    data-testid={`edit-offer-btn-${offer.id}`}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-sm"
                    onClick={() => navigate(`/edit/${offer.id}`)}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Éditer
                  </Button>
                  <Button
                    data-testid={`share-offer-btn-${offer.id}`}
                    variant="outline"
                    size="icon"
                    className="rounded-sm"
                    onClick={() => copyShareLink(offer.share_link)}
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                  <Button
                    data-testid={`delete-offer-btn-${offer.id}`}
                    variant="outline"
                    size="icon"
                    className="rounded-sm hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => handleDelete(offer.id)}
                    disabled={deleting === offer.id}
                  >
                    {deleting === offer.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
